import React, { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { CommandPalette } from "@/components/CommandPalette";
import { AIAssistant } from "@/components/AIAssistant";
import { useAuth } from "@/context/AuthContext";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : true));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

export function AppShell() {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setAiOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (isDesktop && mobileNav) setMobileNav(false);
  }, [isDesktop, mobileNav]);

  useEffect(() => {
    if (!mobileNav) return undefined;
    const onEsc = (e) => e.key === "Escape" && setMobileNav(false);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [mobileNav]);

  const openInquiry = useCallback(() => navigate("/workspace"), [navigate]);
  const openAI = useCallback(() => setAiOpen(true), []);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      {isDesktop && <Sidebar onOpenAI={openAI} />}
      {!isDesktop && mobileNav && (
        <div className="fixed inset-0 z-40 flex lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 animate-in fade-in-0 bg-[hsl(30_12%_10%/0.4)] backdrop-blur-[2px] duration-200" onClick={() => setMobileNav(false)} data-testid="mobile-nav-backdrop" />
          <div className="relative z-50 animate-in slide-in-from-left duration-200">
            <Sidebar onOpenAI={() => { setMobileNav(false); openAI(); }} onNavigate={() => setMobileNav(false)} />
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar user={user} onMenu={() => setMobileNav(true)} onSearch={() => setPaletteOpen(true)} onAI={openAI} onNew={openInquiry} onProfile={() => navigate("/settings")} />
        <main id="main-scroll" className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
          <Outlet context={{ openInquiry, openAI, openPalette: () => setPaletteOpen(true) }} />
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} onNewInquiry={openInquiry} onOpenAI={openAI} />
      <AIAssistant open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}
